// Pantalla «Mi tienda» del Portal Vendedor — réplica del mock `portal-vendedor-tienda.html`.
// Sale de `GET /seller/store-summary?range=` y, para las alertas del piso, de `GET /seller/home`.
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { Badge, ErrorState, Loading } from '@/kit';
import { KpiCell, type KpiItem } from '@/shared/patterns/KpiBar';
import { ScreenBand } from '@/shared/patterns/ScreenBand';
import { TruncatedNotice } from '@/shared/patterns/TruncatedNotice';
import { useScreenTitle } from '@/shared/patterns/AppShell';
import { formatMXN, formatShortMXN } from '@/shared/lib/format/money';
import { plural } from '@/shared/lib/format/plural';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import { cn } from '@/lib/cn';
import { formatDateTimeMx, formatTimeMx } from '@/lib/dateHelpers';
import type { StoreSummary, StoreSummaryRange } from '@/api/services/retail-wizard/contract';
import { saleProductName, SaleTicketProduct } from '@/shared/patterns/SaleTicket';
import { useSellerAlerts, type SellerAlert } from './api/usePortal';
import { useStoreSummary } from './store/api/useStoreSummary';
import {
    STORE_RANGES,
    financierChipOf,
    goalMissingText,
    goalPercent,
    mixPercent,
    prospectsTotalLabel,
    rangeKpiLabel,
    rangeNoun,
    rangeWhen,
    rankingMetaLabel,
    recentSalesLabel,
    saleWhenLabel,
    sellerInitials,
    sellerLabel,
    sortedProspectsBySeller,
    sortedRanking,
    sortedStoreSales,
    sortedTopSkus,
    truncationNotice,
} from './store/lib/storeFormat';
import { HomeCard, HomeEmpty, HomeRow } from './components/HomeCard';
import { AlertsCard } from './components/AlertsCard';

/** Lo que se pinta donde el dato todavía no tiene de dónde salir. */
const NO_SOURCE = '—';

/** Aire lateral de las filas de ESTA pantalla (`.row-dense { padding: 5px 12px }` de su mock). */
const ROW_PADDING = 'px-3';

export function SalesStore() {
    const navigate = useNavigate();
    const [range, setRange] = useState<StoreSummaryRange>('today');
    const summary = useStoreSummary(range);
    const alerts = useSellerAlerts();

    const data = summary.data ?? null;
    const goalPct = data ? goalPercent(data.salesAmountCents, data.dailyGoalCents) : null;
    // Error con datos en mano = refetch fallido sobre cache tibia: se sigue viendo lo viejo.
    const isStale = summary.isError && data !== null;
    const truncated = data ? truncationNotice(data.truncatedSales, data.truncatedProspects) : null;

    // El mock nombra la tienda en el topbar, no en una fila propia dentro de la pantalla.
    useScreenTitle(data?.storeName ? `Mi tienda · ${data.storeName}` : null);

    return (
        <div className="flex h-full min-h-0 flex-col">
            {/* `.kpi-bar` del mock: a sangre y pegada al topbar. Las celdas se mapean acá porque
                `<ScreenBand>` YA es el contenedor de la barra; anidar `<KpiBar>` duplicaría padding. */}
            <ScreenBand className="gap-0">
                {kpiItemsOf(data, range, goalPct).map((item) => (
                    <KpiCell key={item.label} item={item} />
                ))}
            </ScreenBand>

            <RangeBar range={range} onChange={setRange} summary={rangeSummaryOf(data)} />

            {truncated ? (
                <div className="pt-2.5">
                    <TruncatedNotice title={truncated.title} body={truncated.body} />
                </div>
            ) : null}

            {summary.isPending ? <Loading size="lg" /> : null}

            {summary.isError ? (
                <div className="pt-2.5">
                    <ErrorState
                        title="No se pudo cargar el resumen de la tienda"
                        description={summary.error.message}
                        onRetry={() => void summary.refetch()}
                    />
                </div>
            ) : null}

            {isStale ? (
                <p className="pt-2.5 text-center text-[10.5px] text-muted-foreground">
                    Lo de abajo es la última lectura buena, de las{' '}
                    {formatTimeMx(new Date(summary.dataUpdatedAt).toISOString())}.
                </p>
            ) : null}

            {data ? (
                /* `.tienda-grid` del mock: 2fr 1fr, gap y padding de 10 px, también a sangre. */
                <div
                    className={cn(
                        '-mx-5 -mb-4 grid min-h-0 flex-1 grid-cols-[2fr_1fr] gap-2.5 bg-background p-2.5',
                        isStale && 'opacity-60',
                    )}
                >
                    {/* Columna izquierda — ranking + ventas + SKUs */}
                    <div className="flex min-h-0 flex-col gap-2.5">
                        <RankingCard summary={data} />
                        <StoreSalesCard summary={data} range={range} />
                        <TopSkusCard
                            summary={data}
                            range={range}
                            onPick={() => {
                                void navigate({
                                    to: '/sales/$section',
                                    params: { section: 'catalog' },
                                });
                            }}
                        />
                    </div>

                    {/* Columna derecha — meta + prospectos + stock + alertas */}
                    <div className="flex min-h-0 flex-col gap-2.5">
                        <GoalCard summary={data} range={range} goalPct={goalPct} />
                        <ProspectsCard summary={data} />
                        <CentralStockCard />
                        <FloorAlertsCard
                            alerts={alerts.data ?? []}
                            isPending={alerts.isPending}
                            isError={alerts.isError}
                            onRetry={() => void alerts.refetch()}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Resumen del rango. Con el barrido recortado los totales son un PISO, así que la frase lo dice —
 * un «45 ventas» que en realidad son «45 y las que no se alcanzaron a leer» es un número falso.
 */
function rangeSummaryOf(store: StoreSummary | null): string {
    if (!store) return NO_SOURCE;
    const totals = `${plural(store.salesCount, 'venta', 'ventas')} · ${formatMXN(store.salesAmountCents / 100)}`;
    return store.truncatedSales ? `al menos ${totals}` : totals;
}

/**
 * Los 6 KPIs del mock. Mientras el resumen no llega, TODO sale en «—» y sin nota: explicar por qué
 * falta un dato que sí va a llegar se lee como un diagnóstico equivocado.
 */
function kpiItemsOf(
    store: StoreSummary | null,
    range: StoreSummaryRange,
    goalPct: number | null,
): KpiItem[] {
    if (!store) {
        return [
            { label: rangeKpiLabel(range), value: NO_SOURCE },
            { label: 'Meta tienda', value: NO_SOURCE },
            { label: 'Vendedores activos', value: NO_SOURCE },
            { label: 'Comisiones día', value: NO_SOURCE, tone: 'success' },
            { label: 'Mix SK', value: NO_SOURCE },
            { label: 'SKUs por agotar', value: NO_SOURCE },
        ];
    }

    return [
        {
            label: rangeKpiLabel(range),
            value: store.salesCount,
            sub: formatShortMXN(store.salesAmountCents / 100),
        },
        {
            label: 'Meta tienda',
            value: goalPct === null ? NO_SOURCE : `${String(goalPct)}%`,
            tone: goalPct !== null && goalPct >= 100 ? 'success' : 'default',
            sub:
                goalPct === null || store.dailyGoalCents === null ? (
                    `de ${NO_SOURCE}`
                ) : (
                    <span className="inline-flex items-center gap-1.5">
                        <GoalMiniBar percent={goalPct} />
                        de {formatShortMXN(store.dailyGoalCents / 100)}
                    </span>
                ),
        },
        // El padrón en `null` es «no se pudo leer», y quien pide la pantalla es un vendedor de esta
        // tienda: un «0/0» diría que no tiene ninguno.
        {
            label: 'Vendedores activos',
            value:
                store.sellersActive === null || store.sellersAssigned === null
                    ? NO_SOURCE
                    : `${String(store.sellersActive)}/${String(store.sellersAssigned)}`,
            sub:
                store.sellersActive === null || store.sellersAssigned === null
                    ? 'sin lectura'
                    : 'de asignados',
        },
        // Comisiones: sin tabla que las guarde no hay número, y el «—» ya comunica la ausencia.
        // El verde es del elemento en el mock, no del valor: va puesto siempre.
        { label: 'Comisiones día', value: NO_SOURCE, tone: 'success', sub: 'a pagar al corte' },
        {
            label: 'Mix SK',
            value: `${String(mixPercent(store.surekeepCount, store.salesCount))}%`,
            sub: `${String(store.surekeepCount)} de ${String(store.salesCount)}`,
        },
        // SKUs por agotar: el inventario lo tiene La Central y su integración está pendiente.
        {
            label: 'SKUs por agotar',
            value: NO_SOURCE,
            tone: 'warning',
            sub: 'La Central pendiente',
        },
    ];
}

/** Barrita de avance de la meta del KPI (`.progress-mini` del mock). */
function GoalMiniBar({ percent }: { percent: number }) {
    return (
        <span className="inline-block h-[5px] w-[60px] overflow-hidden rounded-full bg-muted align-middle">
            <span
                className="block h-full rounded-full bg-success"
                style={{ width: `${String(Math.min(percent, 100))}%` }}
            />
        </span>
    );
}

/** Chips de rango + resumen a la derecha (`.range-bar` del mock): segunda franja, pegada a la KPI. */
function RangeBar({
    range,
    onChange,
    summary,
}: {
    range: StoreSummaryRange;
    onChange: (next: StoreSummaryRange) => void;
    summary: string;
}) {
    return (
        <ScreenBand className="mt-0 bg-muted/30 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                Rango:
            </span>
            {STORE_RANGES.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    aria-pressed={range === option.value}
                    onClick={() => {
                        onChange(option.value);
                    }}
                    className={cn(
                        'flex-none rounded-full border px-2.5 py-[3px] text-[11px] transition-colors',
                        range === option.value
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card hover:bg-accent/50',
                    )}
                >
                    {option.label}
                </button>
            ))}
            <span className="flex-1" />
            <span className="flex-none text-[10.5px] text-muted-foreground">{summary}</span>
        </ScreenBand>
    );
}

/**
 * Ranking sembrado con el padrón: el que no vendió sale en cero y no desaparece. Sin padrón la
 * lista vuelve a ser solo quien vendió, y eso se dice al pie: leerla como la tienda entera es falso.
 */
function RankingCard({ summary }: { summary: StoreSummary }) {
    const rows = sortedRanking(summary.ranking);
    const rosterMissing = summary.sellersAssigned === null;
    return (
        <HomeCard
            title="Ranking de vendedores"
            meta={rankingMetaLabel(rows.length, summary.sellersAssigned)}
            className="flex-none"
            bodyClassName="max-h-[260px]"
        >
            {rows.length === 0 ? (
                <HomeEmpty>
                    {rosterMissing
                        ? 'Sin ventas en el rango, y el padrón de la tienda no se pudo leer.'
                        : 'Esta tienda no tiene vendedores asignados.'}
                </HomeEmpty>
            ) : (
                <>
                    {rows.map((row, index) => {
                        const isMe = row.sellerRetailUserId === summary.mySellerRetailUserId;
                        return (
                            <div
                                key={row.sellerRetailUserId}
                                className={cn(
                                    'flex items-center gap-2 border-b border-border/50 px-3 py-1.5 text-[11.5px] last:border-b-0',
                                    isMe && 'border-l-2 border-l-primary bg-primary/5',
                                )}
                            >
                                <span
                                    className={cn(
                                        'flex size-6 flex-none items-center justify-center rounded-full text-[11px] font-bold',
                                        index === 0
                                            ? 'bg-tier-gold text-white'
                                            : 'bg-muted text-foreground',
                                    )}
                                >
                                    #{index + 1}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="truncate font-medium">
                                            {sellerLabel(row.sellerName, row.sellerRetailUserId)}
                                        </span>
                                        {isMe ? (
                                            <Badge variant="info" size="xs">
                                                Yo
                                            </Badge>
                                        ) : null}
                                    </div>
                                    <div className="mt-px text-[10px] text-muted-foreground">
                                        {row.salesCount === 0
                                            ? 'Sin ventas en el rango'
                                            : plural(row.salesCount, 'venta', 'ventas')}{' '}
                                        · comisión {NO_SOURCE}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-semibold tabular-nums">
                                        {formatMXN(row.amountCents / 100)}
                                    </div>
                                    <div className="mt-px text-[10px] text-muted-foreground">
                                        {NO_SOURCE}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {rosterMissing ? (
                        <p className="px-3 py-1.5 text-[10px] text-warning">
                            No se pudo leer el padrón de la tienda: aquí solo aparecen los
                            vendedores con ventas en el rango, no todos los de la tienda.
                        </p>
                    ) : null}
                </>
            )}
        </HomeCard>
    );
}

function StoreSalesCard({ summary, range }: { summary: StoreSummary; range: StoreSummaryRange }) {
    const rows = sortedStoreSales(summary.sales);
    // La hora sola cabe en los 40 px del mock; la fecha corta de semana y mes no.
    const whenWidth = range === 'today' ? 'w-10' : 'w-14';
    return (
        <HomeCard
            title={`Ventas ${rangeNoun(range)} · todos los vendedores`}
            meta={recentSalesLabel(rows.length, summary.salesCount)}
            className="flex-none"
            bodyClassName="max-h-[260px]"
        >
            {rows.length === 0 ? (
                <HomeEmpty>Sin ventas en el rango.</HomeEmpty>
            ) : (
                rows.map((sale) => {
                    const chip = financierChipOf(sale.type, sale.externalProvider);
                    return (
                        <HomeRow key={sale.saleId} className={ROW_PADDING}>
                            <span
                                className={cn(
                                    'flex-none font-mono text-[10.5px] text-muted-foreground',
                                    whenWidth,
                                )}
                                title={formatDateTimeMx(sale.soldAt)}
                            >
                                {saleWhenLabel(sale.soldAt, range)}
                            </span>
                            <span
                                className="w-[45px] flex-none truncate font-mono text-[10.5px] text-muted-foreground"
                                title={sellerLabel(sale.sellerName, sale.sellerRetailUserId)}
                            >
                                {sellerInitials(sale.sellerName)}
                            </span>
                            <span className="min-w-[100px] flex-1 truncate font-medium">
                                {saleCustomerLabel(sale) ?? NO_SOURCE}
                            </span>
                            <span className="min-w-0 flex-1 text-[10.5px] text-muted-foreground">
                                <SaleTicketProduct
                                    sale={sale}
                                    category={null}
                                    saleName={saleProductName(sale)}
                                />
                            </span>
                            <span className="flex-none">
                                <Badge variant={chip.variant} size="xs">
                                    {chip.label}
                                </Badge>
                            </span>
                            <span className="w-[70px] flex-none text-right font-semibold tabular-nums">
                                {formatMXN(soldCentsOf(sale) / 100)}
                            </span>
                        </HomeRow>
                    );
                })
            )}
        </HomeCard>
    );
}

function TopSkusCard({
    summary,
    range,
    onPick,
}: {
    summary: StoreSummary;
    range: StoreSummaryRange;
    onPick: () => void;
}) {
    const rows = sortedTopSkus(summary.topSkus);
    return (
        <HomeCard
            title={`SKUs top de la tienda ${rangeWhen(range)}`}
            meta={plural(rows.length, 'SKU', 'SKUs')}
            className="flex-1"
        >
            {rows.length === 0 ? (
                <HomeEmpty>Sin ventas en el rango.</HomeEmpty>
            ) : (
                rows.map((row, index) => (
                    <HomeRow key={row.sku} onClick={onPick} className={ROW_PADDING}>
                        <span className="w-10 flex-none font-mono text-[10.5px] text-muted-foreground">
                            #{index + 1}
                        </span>
                        <span className="w-10 flex-none truncate font-mono text-[10.5px] text-muted-foreground">
                            {row.sku}
                        </span>
                        <span className="min-w-[100px] flex-1 truncate font-medium">
                            {saleProductName(row)}
                        </span>
                        <span className="flex-1 truncate text-[10.5px] text-muted-foreground">
                            {plural(row.count, 'vendida', 'vendidas')}
                        </span>
                        <span className="w-[70px] flex-none text-right font-semibold tabular-nums">
                            {formatMXN(row.amountCents / 100)}
                        </span>
                    </HomeRow>
                ))
            )}
        </HomeCard>
    );
}

function GoalCard({
    summary,
    range,
    goalPct,
}: {
    summary: StoreSummary;
    range: StoreSummaryRange;
    goalPct: number | null;
}) {
    const soldPesos = summary.salesAmountCents / 100;
    const goalPesos = summary.dailyGoalCents === null ? null : summary.dailyGoalCents / 100;
    const missing = goalPesos === null ? 0 : goalPesos - soldPesos;
    return (
        <HomeCard
            title={`Meta ${rangeNoun(range)} · tienda`}
            meta={rangeWhen(range)}
            className="flex-none"
            bodyClassName="overflow-visible py-0"
        >
            <div className="p-2.5">
                <div className="flex items-baseline justify-between">
                    <div>
                        <span className="text-[18px] font-semibold tabular-nums">
                            {formatMXN(soldPesos)}
                        </span>
                        <span className="text-[11px] text-muted-foreground"> de </span>
                        <span className="text-[12px] font-medium tabular-nums">
                            {goalPesos === null ? NO_SOURCE : formatMXN(goalPesos)}
                        </span>
                    </div>
                    <span className="text-[12px] font-semibold">
                        {goalPct === null ? NO_SOURCE : `${String(goalPct)}%`}
                    </span>
                </div>
                {/* Sin meta no hay avance que dibujar: el riel vacío se lee como un 0% medido. */}
                {goalPct === null ? null : (
                    <div
                        data-testid="store-goal-progress"
                        className="my-1.5 h-2.5 overflow-hidden rounded bg-muted"
                    >
                        <div
                            className="h-full rounded bg-success"
                            style={{ width: `${String(Math.min(goalPct, 100))}%` }}
                        />
                    </div>
                )}
                <div className="text-[10.5px] text-muted-foreground">
                    {goalPct === null
                        ? goalMissingText(range)
                        : missing > 0
                          ? `Faltan ${formatMXN(missing)}`
                          : `¡Meta cumplida! Excedente: ${formatMXN(-missing)}`}
                </div>
            </div>
        </HomeCard>
    );
}

function ProspectsCard({ summary }: { summary: StoreSummary }) {
    const rows = sortedProspectsBySeller(summary.prospectsBySeller);
    return (
        <HomeCard
            title="Prospectos abiertos · tienda"
            meta={prospectsTotalLabel(summary.prospectsOpenTotal, summary.truncatedProspects)}
            className="flex-none"
            bodyClassName="max-h-[180px]"
        >
            {rows.length === 0 ? (
                <HomeEmpty>Sin prospectos abiertos.</HomeEmpty>
            ) : (
                rows.map((row) => (
                    <HomeRow key={row.sellerRetailUserId} className={ROW_PADDING}>
                        <span
                            className="w-[45px] flex-none truncate font-mono text-[10.5px] text-muted-foreground"
                            title={sellerLabel(row.sellerName, row.sellerRetailUserId)}
                        >
                            {sellerInitials(row.sellerName)}
                        </span>
                        <span className="min-w-[100px] flex-1 truncate font-medium">
                            {sellerLabel(row.sellerName, row.sellerRetailUserId)}
                        </span>
                        <span className="flex-1 truncate text-[10.5px] text-muted-foreground">
                            {plural(row.openCount, 'abierto', 'abiertos')}
                        </span>
                        <span className="flex-none">
                            {row.overdueCount > 0 ? (
                                <Badge variant="danger" size="xs">
                                    {row.overdueCount} venc
                                </Badge>
                            ) : (
                                <Badge variant="muted" size="xs">
                                    Al día
                                </Badge>
                            )}
                        </span>
                    </HomeRow>
                ))
            )}
        </HomeCard>
    );
}

/** El inventario y el stock crítico viven en La Central (ERP) y su lectura sigue sin spec. */
function CentralStockCard() {
    return (
        <HomeCard
            title="Stock crítico · La Central"
            meta="integración pendiente"
            className="flex-none"
            bodyClassName="overflow-visible py-0"
        >
            <div className="border-l-[3px] border-warning bg-warning-soft/40 px-3 py-2.5 text-[11px]">
                <p className="mb-1 text-[9.5px] font-semibold uppercase tracking-[0.05em] text-warning">
                    Fuente: La Central (ERP)
                </p>
                <p>
                    El inventario, alertas de stock crítico y traslados entre tiendas vienen de La
                    Central. La integración de solo lectura está pendiente de spec.
                </p>
            </div>
        </HomeCard>
    );
}

/** Alertas REALES del piso (`GET /seller/home`). Hoy el silo solo emite la de KYC en revisión. */
function FloorAlertsCard({
    alerts,
    isPending,
    isError,
    onRetry,
}: {
    alerts: SellerAlert[];
    isPending: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    return (
        <AlertsCard
            title="Alertas del piso"
            emptyText="Sin alertas del piso."
            alerts={alerts}
            isPending={isPending}
            isError={isError}
            onRetry={onRetry}
            className="flex-1"
            renderAlert={(alert) => (
                // `.alert-item` del mock: UNA línea. Sin CTA — el KYC lo resuelve Cumplimiento.
                <HomeRow key={alert.id} className="px-3 py-1.5 text-[11px]">
                    <span
                        aria-hidden
                        className="size-4 flex-none rounded-full bg-warning text-center text-[10px] leading-4 text-warning-foreground"
                    >
                        !
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                        {alert.customerPhone} {alert.text}
                    </span>
                </HomeRow>
            )}
        />
    );
}
